/**
 * Global hook to poll resume status across all pages.
 * This ensures that resume generation status updates in real-time
 * even when navigating away from the Resumes page.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { resumeApi } from '@/lib/api';
import type { Resume } from '@/lib/api';

export function useGlobalResumePolling() {
  const queryClient = useQueryClient();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Query to fetch resumes (works even if not rendering Resumes page)
  const { data: resumes = [] } = useQuery({
    queryKey: ['resumes'],
    queryFn: resumeApi.list,
    // Only fetch if there's auth (component can check auth separately)
    staleTime: 0,
  });

  // Setup polling based on resume status
  useEffect(() => {
    const hasInFlight = resumes.some(
      (r) => r.status === 'pending' || r.status === 'generating'
    );

    if (hasInFlight && !pollingRef.current) {
      // Start polling if any resume is in flight
      pollingRef.current = setInterval(() => {
        void queryClient.invalidateQueries({ queryKey: ['resumes'] });
      }, 3000);
    } else if (!hasInFlight && pollingRef.current) {
      // Stop polling if no resumes are in flight
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [resumes, queryClient]);
}
